import * as React from 'react';

import { Input, type InputProps } from '@/ux-common/components/input/input';
import { FIELD_INTERACTIVE_SELECTOR } from '@/ux-common/components/input/input.constants';
import {
  InputFieldProvider,
  useGroupField,
  useInputGroup,
} from '@/ux-common/components/input/input.context';
import { cn } from '@/ux-common/lib/utils';

/** The native resize grip's hit area, at the field's bottom-right corner. */
const RESIZE_GRIP_PX = 16;

/** Whether a press lands on a resizable field's grip. */
function onResizeGrip(field: HTMLElement, x: number, y: number) {
  if (getComputedStyle(field).resize === 'none') return false;
  const box = field.getBoundingClientRect();
  return x >= box.right - RESIZE_GRIP_PX && y >= box.bottom - RESIZE_GRIP_PX;
}

/**
 * Textarea — the multi-line sibling of `Input`, composed the same way.
 *
 * `Textarea` IS an `Input` group carrying a `.textarea` modifier, so the label,
 * helper, `aria-describedby` wiring and every root state modifier (`.input-error`,
 * `.input-warning`, `.input-dirty`, `.input-display`, sizes) come from one
 * implementation rather than a parallel one. Compose `InputLabel` and `InputHelper`
 * inside it exactly as you would for a single-line field.
 *
 * There are **no behavior props** — label, helper and error are slots, and resize is
 * a class. Adding them back as props would fork the label / helper /
 * `aria-describedby` wiring that `Input` already owns, which is how the two
 * components drift apart.
 *
 * @example
 * ```tsx
 * <Textarea>
 *   <InputLabel htmlFor={id}>Notes</InputLabel>
 *   <TextareaField id={id} rows={4} maxLength={200} placeholder="Add a note" />
 *   <InputHelper>Optional helper text</InputHelper>
 * </Textarea>
 *
 * // Error state — the same class API as Input
 * <Textarea className="input-error">
 *   <TextareaField aria-invalid="true" />
 *   <InputHelper>Required</InputHelper>
 * </Textarea>
 * ```
 */
/**
 * An alias for `InputProps`, deliberately, rather than a restatement of it.
 *
 * `Textarea` renders `Input` and spreads into it, so every group-level prop already
 * works at runtime — `state` applies its root class AND publishes on the group
 * context, so a slot inside can read it. A narrower type here hides that: a consumer
 * gets a compile error, falls back to `className="input-error"`, and silently loses
 * the context half. Aliasing is what keeps the two from drifting as `InputProps`
 * grows.
 */
export type TextareaProps = InputProps;

export const Textarea = React.forwardRef<HTMLDivElement, TextareaProps>(
  ({ className, ...props }, ref) => (
    <Input ref={ref} data-component="Textarea" className={cn('textarea', className)} {...props} />
  )
);
Textarea.displayName = 'Textarea';

/**
 * TextareaField — the bordered `.input-field` shell with a `<textarea>` embedded.
 *
 * Mirrors `InputField`: `ref` reaches the `<textarea>`, `className` styles the
 * field container, `controlClassName` the control, native attributes forward to
 * the control, and a press anywhere in the field focuses the control. It joins the enclosing group, so `InputLabel`'s `htmlFor` and
 * `InputHelper`'s description reach it with no wiring at the call site.
 *
 * `resize` is a **class**, not a prop: compose `.textarea-fixed` to lock it.
 */
/** Count + cap, published by `TextareaField` so the counter needs no DOM reads. */
const TextareaCounterContext = React.createContext<{ count: number; cap?: number } | null>(null);

export interface TextareaFieldProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'children'> {
  /** Class(es) for the field container (border/layout). */
  className?: string;
  /** Inline style for the field container. */
  style?: React.CSSProperties;
  /** Class(es) for the embedded `<textarea>` control. */
  controlClassName?: string;
  /** Slots rendered inside the field, after the control — e.g. `TextareaCount`. */
  children?: React.ReactNode;
}

export const TextareaField = React.forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  (
    {
      className,
      style,
      controlClassName,
      children,
      rows = 2,
      id,
      value,
      defaultValue,
      maxLength,
      onChange,
      'aria-describedby': ariaDescribedBy,
      ...controlProps
    },
    ref
  ) => {
    // Read without removing them: the control still needs both.
    const disabled = Boolean(controlProps.disabled);
    const readOnly = Boolean(controlProps.readOnly);

    // Join the group exactly as InputField does (see useGroupField): own id and
    // control handle, registration so `InputLabel` points here, and a merged
    // description, so a consumer pointing at an external error keeps the helper and
    // the character counter announced too.
    const {
      controlId,
      controlRef,
      setControlRef,
      describedBy,
      field: fieldContext,
    } = useGroupField<HTMLTextAreaElement>({
      id,
      ref,
      disabled,
      readOnly,
      ariaDescribedBy,
    });

    // A CONTROLLED field derives its length from `value` during render and is always
    // right. The earlier implementation read the DOM from an `input` listener for
    // both paths, which missed every value change React makes itself — an async load,
    // a programmatic clear — and left the counter permanently stale.
    //
    // An UNCONTROLLED field has no such source: React does not own the value, so the
    // count is state fed from `onChange`. That covers typing and nothing else, and
    // the gap is not theoretical — see the reset handling below.
    const isControlled = value !== undefined;
    const [uncontrolledCount, setUncontrolledCount] = React.useState(
      () => String(defaultValue ?? '').length
    );
    const count = isControlled ? String(value ?? '').length : uncontrolledCount;

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!isControlled) setUncontrolledCount(event.target.value.length);
      onChange?.(event);
    };

    // A native `form.reset()` restores the control's value WITHOUT firing `change` or
    // `input` — that is the spec, not a quirk — so an uncontrolled counter keeps the
    // pre-reset number until the user types again. Measured: type to 10 characters in
    // a field with `defaultValue="abc"`, reset the form, and the value is `abc` while
    // the counter still reads `10/50`.
    //
    // It is worse than a stale number on screen. `TextareaCount` registers into the
    // control's `aria-describedby`, so a screen reader announces the wrong remaining
    // budget on focus.
    //
    // Deferred to the next frame on purpose: `reset` fires BEFORE the control's value
    // is restored, so reading synchronously here returns the value being replaced.
    //
    // This closes the reset path only. A direct write to `ref.current.value` still
    // desyncs and there is no event to hang off — an uncontrolled field is
    // fundamentally a value React does not own. Control the field when the value
    // changes by any route other than typing.
    React.useEffect(() => {
      if (isControlled) return;
      const form = controlRef.current?.form;
      if (!form) return;

      let frame = 0;
      const handleReset = () => {
        frame = requestAnimationFrame(() =>
          setUncontrolledCount(controlRef.current?.value.length ?? 0)
        );
      };

      form.addEventListener('reset', handleReset);
      return () => {
        if (frame) cancelAnimationFrame(frame);
        form.removeEventListener('reset', handleReset);
      };
      // `controlRef` is a stable ref object from useGroupField; listing it changes
      // nothing, it only tells the lint rule so.
    }, [isControlled, controlRef]);

    const counter = React.useMemo(() => ({ count, cap: maxLength }), [count, maxLength]);

    // Mirrors InputField. `.input-field.textarea-field` adds block padding, so there
    // is inert chrome inside the visible border; without this, a press there focuses
    // nothing while the identical press on an Input focuses its control.
    const handleFieldMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
      // Primary button only — a right-click would otherwise steal focus and collapse
      // a selection the user was about to act on through the context menu.
      if (event.button !== 0) return;

      const control = controlRef.current;
      // `readOnly` alongside `disabled`: consumers use it for "no permission to
      // edit", so focusing one advertises editability it does not have, and the
      // preventDefault() below would suppress the drag-select that is the one useful
      // interaction on a read-only value.
      if (!control || control.disabled || control.readOnly) return;

      const field = event.currentTarget;
      const target = event.target as HTMLElement | null;
      if (!target || target === control) return; // direct hit — let the browser do it

      // `closest()` walks past the field, so bound the match to it: an interactive
      // ancestor *outside* the field is not the user pressing a slot.
      const interactive = target.closest(FIELD_INTERACTIVE_SELECTOR);
      if (interactive && field.contains(interactive)) return;

      // The resize grip is the field's own (Figma draws it at the field's corner), and
      // preventDefault() below would cancel the drag. Leave a press on it alone.
      if (target === field && onResizeGrip(field, event.clientX, event.clientY)) return;

      event.preventDefault();
      control.focus();

      // Vertical, not horizontal as in InputField: this control is multi-line, so the
      // meaningful axis for "before the text" vs "after it" is Y. A press in the top
      // padding starts at the beginning, anywhere else continues at the end.
      const caret = event.clientY < control.getBoundingClientRect().top ? 0 : control.value.length;
      control.setSelectionRange(caret, caret);
    };

    return (
      <TextareaCounterContext.Provider value={counter}>
        {/* The field is not an interactive element and gets no role: the handler only
            widens the pointer target for the embedded <textarea>, which remains the
            sole focusable, keyboard-operable control. Nothing here is mouse-exclusive. */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          data-component="TextareaField"
          className={cn('input-field', 'textarea-field', className)}
          style={style}
          onMouseDown={handleFieldMouseDown}
        >
          <textarea
            ref={setControlRef}
            id={controlId}
            rows={rows}
            maxLength={maxLength}
            data-component="TextareaControl"
            className={cn('input-control', 'textarea-control', controlClassName)}
            aria-describedby={describedBy}
            onChange={handleChange}
            {...(isControlled ? { value } : { defaultValue })}
            {...controlProps}
          />
          <InputFieldProvider value={fieldContext}>{children}</InputFieldProvider>
        </div>
      </TextareaCounterContext.Provider>
    );
  }
);
TextareaField.displayName = 'TextareaField';

/**
 * TextareaCount — the inline "x/y Characters" counter.
 *
 * A slot rather than a prop, so it is opt-in and positioned by composition. Reads
 * the control it sits beside through the group, so it needs no value wiring: pass
 * `maxLength` on `TextareaField` and drop this inside it.
 *
 * Sits in a strip of the field's padding below the control, where nothing scrolls, so it
 * paints no background and is right on any surface (UXD-171).
 *
 * Deliberately NOT `aria-live` — but it IS registered into the control's
 * `aria-describedby`, which are two separate decisions:
 *
 * - Not live, because the count changes on every keystroke and announcing each
 *   change floods the speech queue and fights the typing echo.
 * - Still described, because the limit has to be discoverable. It is announced once
 *   on focus, like any other helper.
 *
 * An earlier revision did neither, on the reasoning that "maxLength is what
 * assistive tech announces". That is false: HTML `maxlength` has no mapping into
 * any accessibility API, and no screen reader announces it. Nothing compensated,
 * so the limit was unreachable.
 */
export interface TextareaCountProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Character cap to display. Falls back to the control's `maxLength`. */
  maxLength?: number;
}

export const TextareaCount = React.forwardRef<HTMLSpanElement, TextareaCountProps>(
  ({ className, maxLength, id, ...props }, ref) => {
    const group = useInputGroup();
    const counter = React.useContext(TextareaCounterContext);
    const ownId = React.useId();
    const effectiveId = id ?? ownId;

    const limit = maxLength ?? counter?.cap;
    const registerHelper = group?.registerHelper;
    // The SAME condition as the `return null` below, deliberately. These used to
    // diverge — registration asked only for a limit, rendering asked for a limit and
    // a field — so a count composed inside a `Textarea` group but outside a
    // `TextareaField`, with an explicit `maxLength`, registered an id and then
    // rendered nothing. The control's `aria-describedby` then pointed at an element
    // that does not exist, and a screen reader announces a description it cannot
    // resolve rather than the limit.
    const shouldDescribe = counter != null && limit != null;

    // Joins the control's accessible description, so the remaining budget is
    // announced on FOCUS. That is separable from being a live region: the count
    // changes every keystroke, and announcing each change would interrupt
    // continuously — but a user still has to be able to discover the limit.
    React.useEffect(() => {
      if (!registerHelper || !shouldDescribe) return;
      return registerHelper(effectiveId);
    }, [registerHelper, shouldDescribe, effectiveId]);

    // Requires the field, not just a cap. With a `maxLength` prop but no enclosing
    // `TextareaField` there is nothing to count, and the old condition rendered a
    // confident, permanently-frozen `0/99`. Nothing is more honest than a wrong
    // number. Kept in sync with `shouldDescribe` above.
    if (counter == null || limit == null) return null;

    return (
      <span
        ref={ref}
        id={effectiveId}
        data-component="TextareaCount"
        className={cn('textarea-count', className)}
        {...props}
      >
        {counter?.count ?? 0}/{limit} Characters
      </span>
    );
  }
);
TextareaCount.displayName = 'TextareaCount';
